import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Quote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import Logo from "../components/Logo";

const COAST_IMG =
  "https://images.unsplash.com/photo-1784646450786-bc07a20d3b81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxzb3V0aHdlc3QlMjBkZXNlcnQlMjBkdW5lcyUyMG9jZWFuJTIwY29hc3QlMjBsYW5kc2NhcGUlMjBzdW5zZXR8ZW58MHx8fHwxNzkxODMzNDk4fDA&ixlib=rb-4.1.0&q=85";

export default function AboutPage() {
  const { t, lang } = useLang();
  const missionQuote = lang === "es" && t.about.missionQuoteEs ? t.about.missionQuoteEs : t.about.missionQuote;
  return (
    <main data-testid="about-page">
      <Seo title={t.seo.about.title} desc={t.seo.about.desc} />
      <PageHero eyebrow={t.about.eyebrow} title={t.about.title} />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Mission */}
          <div className="grid items-start gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow>{t.about.missionTitle}</Eyebrow>
                <blockquote
                  className="relative mt-6 rounded-2xl border-l-4 border-terracotta bg-cream/70 p-7 sm:p-9"
                  data-testid="about-mission-quote"
                >
                  <Quote className="absolute -top-4 left-6 h-8 w-8 rounded-full bg-terracotta p-1.5 text-white" />
                  <p className="font-serif text-xl sm:text-2xl font-medium leading-relaxed text-espresso">
                    {missionQuote}
                  </p>
                </blockquote>
                <div className="mt-6 space-y-5">
                  {t.about.mission.map((p, i) => (
                    <p key={i} className="text-base sm:text-lg leading-relaxed text-espresso/85">{p}</p>
                  ))}
                </div>
              </Reveal>

              {/* Values */}
              <div className="mt-14">
                <Reveal>
                  <Eyebrow>{t.about.valuesTitle}</Eyebrow>
                </Reveal>
                <div className="mt-6 grid gap-5 sm:grid-cols-3">
                  {t.about.values.map((v, i) => (
                    <Reveal key={v.title} delay={0.07 * i}>
                      <div className="h-full rounded-2xl border border-sand/50 bg-cream/60 p-6" data-testid={`about-value-${i}`}>
                        <span className="font-serif text-3xl font-semibold text-terracotta/70">0{i + 1}</span>
                        <h3 className="mt-3 font-serif text-lg font-medium text-espresso">{v.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-espresso/70">{v.text}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="arch-frame overflow-hidden shadow-xl">
                  <img
                    src={COAST_IMG}
                    alt="Desert coastline where dunes meet the ocean"
                    className="h-80 w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 flex justify-center rounded-2xl border border-sand/50 bg-cream p-6">
                  <Logo withText size={72} />
                </div>
              </Reveal>
            </div>
          </div>

          {/* Story + Founder */}
          <div className="mt-16 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-sand/50 bg-cream/60 p-8 sm:p-10" data-testid="about-story-card">
                <Eyebrow>{t.about.storyTitle}</Eyebrow>
                <div className="mt-5 space-y-4">
                  {t.about.story.map((p, i) => (
                    <p key={i} className="text-sm sm:text-base leading-relaxed text-espresso/85">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-6">
                <div className="rounded-2xl bg-espresso p-8 sm:p-10 grain relative overflow-hidden" data-testid="about-founder-card">
                  <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-terracotta/25 blur-2xl" aria-hidden="true" />
                  <div className="relative">
                    <Eyebrow light>{t.about.founderTitle}</Eyebrow>
                    <p className="mt-5 font-serif text-xl font-medium leading-snug text-sand" data-testid="about-founder-role">
                      {t.about.founderRole}
                    </p>
                    {t.about.founder.map((p, i) => (
                      <p key={i} className="mt-4 text-sm sm:text-base leading-relaxed text-cream/80">{p}</p>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-teal/30 bg-teal/5 p-7" data-testid="about-location-card">
                  <div className="flex items-center gap-2 text-teal">
                    <MapPin className="h-4 w-4" />
                    <h3 className="font-serif text-lg font-medium text-espresso">{t.about.locationTitle}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-espresso/80">{t.about.locationPlaceholder}</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16 text-center">
            <Link
              to="/contact"
              data-testid="about-contact-button"
              className="group inline-flex items-center gap-2 rounded-full bg-espresso px-8 py-4 text-base font-semibold text-cream transition-colors duration-300 hover:bg-terracotta active:scale-95"
            >
              {t.common.getStarted}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
